import { CanActivateFn, Router } from '@angular/router';
import { computed, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, first, map } from 'rxjs';
import { AppStore } from './store';
import { DemoPersonaSwitchService } from './demo/demo-persona-switch.service';
import { personaSwitchAppliesToHost } from './demo/persona-switch.model';

/**
 * Route guard for terminal / error pages: /signed-out, /error, and any sibling
 * page that must never be the post-login destination.
 *
 * INVARIANT (owner rule): "error / terminal pages must NEVER be the post-login
 * destination — always redirect to /home."
 *
 * When a user IS authenticated (has a live BFF session) and the router tries to
 * activate a terminal route (e.g. from the initial navigation on the debug host
 * where the browser URL is still /signed-out after a logout), this guard
 * redirects them to /home before the route component ever mounts.
 *
 * When the user is NOT authenticated the guard allows the route normally, so a
 * logged-out user still sees the sign-out or error screen as intended.
 *
 * WHY a guard and not just afterLogin() alone
 * -------------------------------------------
 * The existing afterLogin() check fires navigateByUrl('/home') after the async
 * /api/me call resolves. On the debug host, the Angular router's INITIAL
 * navigation to the browser URL (/signed-out) is already in progress when
 * <tb-root> mounts. Because the initial navigation fires before the HTTP
 * response arrives, it can race with — and in some orderings override — the
 * afterLogin() navigation. A route guard is evaluated INSIDE the router's
 * navigation pipeline, so it eliminates the race: the /signed-out route can
 * never fully activate while the user is authenticated.
 *
 * HOW the async wait works
 * ------------------------
 * At the time the router starts its initial navigation to /signed-out, the BFF
 * session check (/api/me) is still in flight — store.sessionChecked() is false
 * and store.user() is null. A synchronous guard would see null and allow the
 * route every time. Instead this guard returns an Observable that:
 *   1. Fast-paths synchronously if the session question is already settled.
 *   2. Otherwise waits for it to settle, then decides — all within the guard,
 *      before the route component mounts. The router holds the navigation open
 *      until the observable emits.
 *
 * WHY "settled" IS NOT THE SAME AS sessionChecked()
 * ------------------------------------------------
 * BUG (owner-reported, reproduced live on debug.demo.localhost at rel-72e11daa4):
 * a user logs in through the debug picker at /signed-out, the portal opens, and
 * the SIGN-OUT SCREEN renders inside it — permanently. Measured timeline after
 * the picker's Continue click:
 *
 *     161 ms  <tb-root> mounts (DebugShellComponent.enterPortal)
 *     179 ms  GET /bff/demo/session 200  -> rootSessionDetected
 *      ~then  THIS GUARD DECIDED, store.user() still null -> route ALLOWED
 *     883 ms  GET /api/me 200            -> store.user() set, 700 ms too late
 *
 * The middle line is measured, not inferred: an instrumented build of this guard
 * logged `decide sessionChecked=true user=false` at the /bff/demo/session tick,
 * before /api/me had answered. The same build with the fix below logs
 * `decide sessionChecked=true user=true` and lands on /home.
 *
 * On a demo host (demo.localhost / debug.demo.localhost) app.component.ts flips
 * sessionChecked TRUE the moment the BFF reports a live ROOT session — see its
 * `state === 'loading' && rootSessionDetected` branch — which is deliberately
 * BEFORE the persona is verified, i.e. before /api/me has even been issued and
 * therefore while store.user() is still null. Reading that instant as "no user"
 * made this guard ALLOW /signed-out for a user who had just authenticated.
 *
 * That is also why the stick is PERMANENT rather than a flash: first() takes the
 * decision once, and nothing re-runs a guard for a route that is already active.
 * store.user() becomes non-null 700 ms later and no one asks the question again.
 * The two earlier defenses cannot save it either — DebugShellComponent's
 * enterPortal() reads router.url, which is still '/' because THIS navigation is
 * the one being held pending; and AppComponent.afterLogin() never runs on a demo
 * host (ngOnInit returns early into the demo bootstrap).
 *
 * FIX: wait for the session question to have a FINAL answer, not merely a first
 * one. On a demo host, `bootstrapState() === 'loading' && !user()` is exactly the
 * transient window in which null does not yet mean "signed out", so hold the
 * navigation across it. This mirrors the identical wait authShellGuard already
 * carries (see its `hostApplies && bootstrapState() === 'loading'` branch) — the
 * two guards are twins and had drifted apart. Non-demo hosts are unaffected:
 * loginViaBffSession() sets user and sessionChecked together, so `settled` is
 * true on the same tick sessionChecked flips (verified live on
 * debug.toybank.localhost, which lands correctly on /home).
 *
 * A genuinely signed-out visitor is NOT held: with no session the demo boot probe
 * answers `{authenticated:false}` (or 401 for a rejected session) and the bootstrap
 * settles to 'signed-out' (a failed probe settles to 'unavailable'); either leaves
 * 'loading', so the guard settles with a null user and allows the sign-out screen
 * exactly as before.
 *
 * SOURCE OF TRUTH: the route list covered by this guard is derived from
 * NON_RESUMABLE_URL_PREFIXES in post-login-redirect.ts so that the guard and
 * the denylist can never drift apart. Apply this guard to every route whose
 * path matches a non-resumable prefix (see app.routes.ts).
 */
export const terminalPageGuard: CanActivateFn = () => {
  const store = inject(AppStore);
  const router = inject(Router);
  const demoPersonaSwitcher = inject(DemoPersonaSwitchService);

  const demoHostApplies =
    typeof window !== 'undefined' && personaSwitchAppliesToHost(window.location.hostname);

  /**
   * True once "is there a signed-in user?" can no longer change from null to
   * non-null under us. Deliberately NOT just sessionChecked() — see the
   * "settled" section above for the demo-host window this closes.
   */
  const sessionSettled = computed(() => {
    if (!store.sessionChecked()) return false;
    // Demo-host bootstrap in flight and no verified persona yet: null is "not
    // known", not "not signed in". Releasing on either signal is safe — a user
    // appearing answers the question, and leaving 'loading' (ready/error) means
    // the bootstrap has said all it is going to say.
    if (demoHostApplies && demoPersonaSwitcher.bootstrapState() === 'loading' && !store.user()) {
      return false;
    }
    return true;
  });

  const decide = () => (store.user() ? router.createUrlTree(['/home']) : true);

  // Fast path: already settled — decide synchronously (no observable overhead).
  if (sessionSettled()) {
    return decide();
  }

  // Still in flight. Return an Observable; the router holds the navigation open
  // until it emits, so the terminal route can never activate under an
  // authenticated user.
  return toObservable(sessionSettled).pipe(
    filter(Boolean),        // wait until the session question is settled
    first(),                // complete after one emission
    map(decide),
  );
};
